import { moneyToNumber } from '@ifixit/helpers';
import type { Cart } from './types';

const SUBTOTAL_THRESHOLD = 500;
const SUBTOTAL_THRESHOLD_CURRENCY = 'USD';
const DISTINCT_LINE_ITEMS_THRESHOLD = 5;
const MAX_QUANTITY_PER_LINE_THRESHOLD = 3;

/**
 * Whether the cart should advertise iFixit Pro.
 *
 * v1 is a deliberately coarse classifier: cart subtotal, distinct line count,
 * or a high quantity on any single line. Pro-eligibility per SKU isn't
 * available client-side (the price_tiers metafield was locked down in
 * #59685), so we approximate with cart-shape signals and iterate from data.
 *
 * Keeping the rule in one function lets us swap the policy — feature flag,
 * server-side eligibility endpoint, category-aware rules — without touching
 * the cart, drawer, or PDP components.
 */
export function wantsProBanner(cart: Cart): boolean {
   const { lineItems } = cart;
   if (lineItems.length === 0) return false;
   const maxQuantityPerLine = Math.max(...lineItems.map(line => line.quantity));
   return (
      maxQuantityPerLine >= MAX_QUANTITY_PER_LINE_THRESHOLD ||
      lineItems.length >= DISTINCT_LINE_ITEMS_THRESHOLD ||
      meetsSubtotalThreshold(cart)
   );
}

/**
 * Subtotal is currency-blind, so we only apply the threshold to USD carts —
 * a raw `amount >= 500` would otherwise treat €500 and £500 the same as $500.
 * Non-USD carts can still trigger the banner via the quantity / distinct-count
 * rules. Per-currency thresholds are a follow-up.
 */
function meetsSubtotalThreshold(cart: Cart): boolean {
   if (cart.totals.price.currencyCode.toUpperCase() !== SUBTOTAL_THRESHOLD_CURRENCY) {
      return false;
   }
   return moneyToNumber(cart.totals.price) >= SUBTOTAL_THRESHOLD;
}
